import { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tabs,
  Tab,
  Chip,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// Module-level variable to persist scroll position across component remounts
// This is necessary because the parent ChatRuntimeWrapper uses key={runtimeKey}
// which causes this component to be completely remounted when switching threads
let savedScrollPosition = null;

// Module-level variable to persist scroll position during delete operations
// (delete doesn't cause remount, but we need to restore after React re-renders)
let deleteScrollPosition = null;

// Module-level variable to persist active tab across component remounts
let savedActiveTab = 0;

// Format date helper
const formatDate = (timestamp) => {
  if (!timestamp) return '';

  const now = Math.floor(Date.now() / 1000);
  const diffSecs = now - timestamp;
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffSecs / 3600);
  const diffDays = Math.floor(diffSecs / 86400);

  if (diffSecs < 0 || diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return new Date(timestamp * 1000).toLocaleDateString();
};

// Get thread preview text
const getThreadPreview = (thread) => {
  if (thread.title) {
    return thread.title.length > 50 ? thread.title.slice(0, 50) + '...' : thread.title;
  }
  return 'New conversation';
};

export const ChatHistory = ({
  threads = [],
  sharedThreads = [], // Threads shared with the current user
  currentThreadId,
  onSelectThread,
  onCloseMobile,
  onRenameThread,
  onDeleteThread,
  onLeaveThread, // Callback when user wants to leave a shared thread
  onSearchChats, // Callback to open search modal
  onPinThread, // Callback to pin/unpin a thread
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const [activeTab, setActiveTab] = useState(savedActiveTab); // 0 = Mine, 1 = Shared (persisted across remounts)
  const scrollContainerRef = useRef(null);

  // Handle tab change and persist to module-level variable
  const handleTabChange = (e, newValue) => {
    savedActiveTab = newValue;
    setActiveTab(newValue);
  };

  // Determine which threads to display based on active tab
  const displayedThreads = activeTab === 0 ? threads : sharedThreads;

  // Restore scroll position on mount (after component remounts due to runtimeKey change)
  useEffect(() => {
    if (savedScrollPosition !== null && scrollContainerRef.current) {
      const targetScroll = savedScrollPosition;
      // Use multiple attempts to handle async rendering
      const restoreScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = targetScroll;
        }
      };
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 50);
      setTimeout(restoreScroll, 100);
      setTimeout(() => {
        restoreScroll();
        savedScrollPosition = null; // Clear after restoration
      }, 200);
    }
  }, []); // Run once on mount

  // Restore scroll position after delete (threads change but no remount)
  useEffect(() => {
    if (deleteScrollPosition !== null && scrollContainerRef.current) {
      const targetScroll = deleteScrollPosition;
      const restoreScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = targetScroll;
        }
      };
      // Restore immediately and with multiple attempts
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(() => {
        restoreScroll();
        deleteScrollPosition = null; // Clear after restoration
      }, 50);
    }
  }, [threads.length]); // Run when threads count changes (after delete)

  const handleSelectThread = async (threadId) => {
    // Save scroll position to module-level variable (survives remount)
    savedScrollPosition = scrollContainerRef.current?.scrollTop || 0;

    // Always call onSelectThread - it will reload if same thread is clicked
    await onSelectThread(threadId);

    // Close mobile sidebar after selection
    if (window.innerWidth <= 768 && onCloseMobile) {
      onCloseMobile();
    }
  };

  const handleMenuOpen = (e, thread) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuThread(thread);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuThread(null);
  };

  const handleRename = async () => {
    if (!menuThread) return;
    const newTitle = prompt('Enter new name:', getThreadPreview(menuThread));
    if (newTitle && newTitle.trim()) {
      await onRenameThread(menuThread.id, newTitle.trim());
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!menuThread) return;
    // Save scroll position to module-level variable before delete
    // This will be restored by the useEffect when threads.length changes
    deleteScrollPosition = scrollContainerRef.current?.scrollTop || 0;
    await onDeleteThread(menuThread.id);
    handleMenuClose();
  };

  const handleLeave = async () => {
    if (!menuThread || !onLeaveThread) return;
    deleteScrollPosition = scrollContainerRef.current?.scrollTop || 0;
    await onLeaveThread(menuThread.id);
    handleMenuClose();
  };

  const handlePin = async () => {
    if (!menuThread || !onPinThread) return;
    await onPinThread(menuThread.id);
    handleMenuClose();
  };

  // Check if the menu thread is a shared thread (not owned by user)
  const isSharedThread = menuThread && sharedThreads.some(t => t.id === menuThread.id);

  // Separate pinned and unpinned threads for display with separator
  const pinnedThreads = displayedThreads.filter(t => t.pinned);
  const unpinnedThreads = displayedThreads.filter(t => !t.pinned);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 150, // Ensure History section always has minimum space
        overflow: 'hidden',
      }}
    >
      {/* Header with Tabs */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            History
          </Typography>
          {onSearchChats && (
            <IconButton
              size="small"
              onClick={onSearchChats}
              sx={{ p: 0.5, color: 'text.secondary' }}
              title="Search Chats"
            >
              <SearchIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>

        {/* Tabs for Mine / Shared */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            style: {
              backgroundColor: activeTab === 1 ? '#f97316' : undefined, // Orange only for Shared tab
            },
          }}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
            },
          }}
        >
          <Tab
            label="Mine"
            sx={{
              '&.Mui-selected': {
                color: 'text.primary',
              },
            }}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon sx={{ fontSize: 14, color: activeTab === 1 ? '#f97316' : 'inherit' }} />
                Shared
                {sharedThreads.length > 0 && (
                  <Chip
                    label={sharedThreads.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      bgcolor: activeTab === 1 ? 'rgba(249, 115, 22, 0.15)' : 'action.hover',
                      color: activeTab === 1 ? '#f97316' : 'text.secondary',
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              '&.Mui-selected': {
                color: '#f97316',
              },
            }}
          />
        </Tabs>
      </Box>

      {/* Scrollable Thread List */}
      <Box
        ref={scrollContainerRef}
        id="historyScroll"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          minHeight: 0,
          overflowAnchor: 'none',
        }}
      >
        <List disablePadding id="historyList">
          {displayedThreads.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.disabled', py: 4 }}
            >
              {activeTab === 0 ? 'No conversations yet' : 'No shared conversations'}
            </Typography>
          ) : (
            <>
              {/* Pinned threads */}
              {pinnedThreads.map((thread) => {
                const isInSharedTab = activeTab === 1;
                const isSharedByMe = activeTab === 0 && thread.shared_count > 0;

                return (
                  <ListItemButton
                    key={thread.id}
                    selected={currentThreadId === thread.id}
                    onClick={() => handleSelectThread(thread.id)}
                    dense
                    sx={{
                      borderRadius: 1,
                      mb: 0.25,
                      py: 0.25,
                      px: 1,
                      minHeight: 0,
                      position: 'relative',
                      borderLeft: isInSharedTab ? '3px solid #f97316' : '3px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <PushPinIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
                    <ListItemText
                      primary={getThreadPreview(thread)}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {formatDate(thread.created_timestamp)}
                          {isSharedByMe && (
                            <PeopleIcon sx={{ fontSize: 12, color: '#f97316' }} />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 500,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        component: 'div',
                      }}
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, thread)}
                      sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        flexShrink: 0,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </ListItemButton>
                );
              })}

              {/* Separator between pinned and unpinned */}
              {pinnedThreads.length > 0 && unpinnedThreads.length > 0 && (
                <Divider sx={{ my: 1 }} />
              )}

              {/* Unpinned threads */}
              {unpinnedThreads.map((thread) => {
                const isInSharedTab = activeTab === 1;
                const isSharedByMe = activeTab === 0 && thread.shared_count > 0;

                return (
                  <ListItemButton
                    key={thread.id}
                    selected={currentThreadId === thread.id}
                    onClick={() => handleSelectThread(thread.id)}
                    dense
                    sx={{
                      borderRadius: 1,
                      mb: 0.25,
                      py: 0.25,
                      px: 1,
                      minHeight: 0,
                      position: 'relative',
                      borderLeft: isInSharedTab ? '3px solid #f97316' : '3px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemText
                      primary={getThreadPreview(thread)}
                      secondary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {formatDate(thread.created_timestamp)}
                          {isSharedByMe && (
                            <PeopleIcon sx={{ fontSize: 12, color: '#f97316' }} />
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: 500,
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        component: 'div',
                      }}
                      sx={{ mr: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, thread)}
                      sx={{
                        p: 0.5,
                        color: 'text.secondary',
                        flexShrink: 0,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <MoreHorizIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </ListItemButton>
                );
              })}
            </>
          )}
        </List>
      </Box>

      {/* Context Menu for Edit/Delete/Leave */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isSharedThread ? (
          // Shared thread: only show Leave option
          <MenuItem onClick={handleLeave} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Leave conversation
          </MenuItem>
        ) : (
          // Owned thread: show Pin, Rename, and Delete
          <>
            {onPinThread && (
              <MenuItem onClick={handlePin}>
                <ListItemIcon>
                  {menuThread?.pinned ? (
                    <PushPinOutlinedIcon fontSize="small" />
                  ) : (
                    <PushPinIcon fontSize="small" />
                  )}
                </ListItemIcon>
                {menuThread?.pinned ? 'Unpin' : 'Pin'}
              </MenuItem>
            )}
            <MenuItem onClick={handleRename}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              Rename
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};
